// scenes/produits/ProduitForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  Numbers as NumbersIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
  LocalOffer as LocalOfferIcon,
  Scale as ScaleIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { produitService } from 'services/produitService';

const ProduitForm = ({ open, onClose, onSuccess, produit, isEditing, categories }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // États pour les conditionnements
  const [conditionnements, setConditionnements] = useState([]);
  const [currentConditionnement, setCurrentConditionnement] = useState({
    unite: '',
    quantite_unitaire: 1,
    prix_achat: '',
    prix_vente: '',
    code_barre: '',
  });

  // États du formulaire principal
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: '',
    categorie: '',
    prix_achat: '',
    prix_vente: '',
    stock_minimum: 0,
    stock_actuel: 0,
    est_actif: true,
    image: null,
  });

  // Réinitialiser le formulaire
  useEffect(() => {
    if (open) {
      if (isEditing && produit) {
        setFormData({
          nom: produit.nom || '',
          code: produit.code || '',
          description: produit.description || '',
          categorie: produit.categorie?.id || '',
          prix_achat: produit.prix_achat || '',
          prix_vente: produit.prix_vente || '',
          stock_minimum: produit.stock_minimum || 0,
          stock_actuel: produit.stock_actuel || 0,
          est_actif: produit.est_actif !== undefined ? produit.est_actif : true,
          image: null,
        });

        setConditionnements(produit.conditionnements || []);
        setImagePreview(produit.image || null);
      } else {
        setFormData({
          nom: '',
          code: '',
          description: '',
          categorie: '',
          prix_achat: '',
          prix_vente: '',
          stock_minimum: 0,
          stock_actuel: 0,
          est_actif: true,
          image: null,
        });
        setConditionnements([]);
        setImagePreview(null);
      }
      setSelectedImage(null);
      setError('');
    }
  }, [open, isEditing, produit]);

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Gestion de l'image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('L\'image est trop volumineuse (max 5MB)');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image valide');
        return;
      }

      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: null }));
  };

  // Gestion des conditionnements
  const handleConditionnementChange = (e) => {
    const { name, value } = e.target;
    setCurrentConditionnement(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const addConditionnement = () => {
    if (!currentConditionnement.unite.trim()) {
      setError('L\'unité du conditionnement est obligatoire');
      return;
    }

    const newConditionnement = {
      ...currentConditionnement,
      id: Date.now(), // ID temporaire pour React
      quantite_unitaire: parseFloat(currentConditionnement.quantite_unitaire) || 1,
      prix_achat: parseFloat(currentConditionnement.prix_achat) || 0,
      prix_vente: parseFloat(currentConditionnement.prix_vente) || 0,
    };

    setConditionnements(prev => [...prev, newConditionnement]);
    setCurrentConditionnement({
      unite: '',
      quantite_unitaire: 1,
      prix_achat: '',
      prix_vente: '',
      code_barre: '',
    });
  };

  const removeConditionnement = (index) => {
    setConditionnements(prev => prev.filter((_, i) => i !== index));
  };

  const editConditionnement = (index) => {
    const cond = conditionnements[index];
    setCurrentConditionnement(cond);
    removeConditionnement(index);
  };

  // Validation du formulaire
  const validateForm = () => {
    if (!formData.nom.trim()) {
      setError('Le nom du produit est obligatoire');
      return false;
    }

    if (formData.prix_vente && parseFloat(formData.prix_vente) < 0) {
      setError('Le prix de vente ne peut pas être négatif');
      return false;
    }

    if (formData.stock_minimum < 0) {
      setError('Le stock minimum ne peut pas être négatif');
      return false;
    }

    // Vérifier que tous les conditionnements ont une unité
    for (const cond of conditionnements) {
      if (!cond.unite.trim()) {
        setError('Tous les conditionnements doivent avoir une unité');
        return false;
      }
    }

    return true;
  };

  // Calculer la marge
  const calculateMarge = () => {
    if (!formData.prix_achat || !formData.prix_vente) return 0;
    const prixAchat = parseFloat(formData.prix_achat);
    const prixVente = parseFloat(formData.prix_vente);
    if (prixAchat === 0) return 0;
    return (((prixVente - prixAchat) / prixAchat) * 100).toFixed(2);
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Dans handleSubmit, change cette partie :

const dataToSend = {
  ...formData,
  prix_achat: formData.prix_achat ? parseFloat(formData.prix_achat) : null,
  prix_vente: formData.prix_vente ? parseFloat(formData.prix_vente) : null,
  stock_minimum: parseInt(formData.stock_minimum) || 0,
  stock_actuel: parseInt(formData.stock_actuel) || 0,
  
  // ✅ CONVERTIR ARRAY EN OBJET
  conditionnements: conditionnements.reduce((acc, cond) => {
    acc[cond.unite] = {
      quantite: cond.quantite_unitaire,
      prix_achat: cond.prix_achat,
      prix_vente: cond.prix_vente,
      code_barre: cond.code_barre || '',
    };
    return acc;
  }, {}),
};
console.log('Données à envoyer:', dataToSend);
      if (selectedImage) {
        dataToSend.image = selectedImage;
      } else if (imagePreview === null && isEditing) {
        dataToSend.image = null; // Pour supprimer l'image
      }

      if (isEditing) {
        await produitService.updateProduit(produit.id, dataToSend);
      } else {
        await produitService.createProduit(dataToSend);
      }
      
      onSuccess();
    } catch (err) {
      console.error('Erreur de soumission:', err);
      
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.nom) {
          setError(`Nom: ${Array.isArray(errorData.nom) ? errorData.nom.join(', ') : errorData.nom}`);
        } else if (errorData.code) {
          setError(`Code: ${Array.isArray(errorData.code) ? errorData.code.join(', ') : errorData.code}`);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else if (errorData.non_field_errors) {
          setError(Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors.join(', ') 
            : errorData.non_field_errors
          );
        } else {
          setError('Une erreur est survenue lors de l\'enregistrement');
        }
      } else {
        setError(err.message || 'Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <InventoryIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              {isEditing ? 'Modifier le produit' : 'Nouveau produit'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Image et informations de base */}
            <Grid item xs={12} md={4}>
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Image du produit
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => document.getElementById('image-upload').click()}
                >
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  
                  {imagePreview ? (
                    <Box>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{
                          width: 150,
                          height: 150,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 2,
                        }}
                      />
                      <Box display="flex" justifyContent="center" gap={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById('image-upload').click();
                          }}
                        >
                          Changer
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                        >
                          Supprimer
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body2" color="textSecondary">
                        Cliquez pour télécharger une image
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        PNG, JPG, JPEG (max 5MB)
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              {/* Informations rapides */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Informations rapides
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_actif}
                      onChange={handleSwitchChange}
                      name="est_actif"
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Produit actif"
                />
                <Typography variant="caption" color="textSecondary">
                  Désactivez pour masquer ce produit des listes de vente
                </Typography>
              </Paper>

              {/* Marge */}
              {formData.prix_achat && formData.prix_vente && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Marge
                  </Typography>
                  <Chip
                    label={`${calculateMarge()}%`}
                    color={calculateMarge() >= 30 ? 'success' : calculateMarge() >= 20 ? 'warning' : 'error'}
                    sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 1 }}
                  />
                </Paper>
              )}
            </Grid>

            {/* Informations principales */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nom du produit *"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InventoryIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Nom commercial du produit"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code produit"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NumbersIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Code unique d'identification"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Catégorie</InputLabel>
                    <Select
                      value={formData.categorie}
                      onChange={handleChange}
                      name="categorie"
                      label="Catégorie"
                      disabled={loading}
                      startAdornment={
                        <InputAdornment position="start">
                          <CategoryIcon />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="">Sélectionner une catégorie</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={3}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Description détaillée du produit"
                  />
                </Grid>
                {/* Conditionnements */}
{/* <Grid item xs={12}>
  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
    Conditionnements
  </Typography>
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Unité</InputLabel>
          <Select
            value={currentConditionnement.unite}
            onChange={(e) => {
              const selectedUnite = e.target.value;
              handleConditionnementChange({
                target: {
                  name: 'unite',
                  value: selectedUnite
                }
              });
              
              if (selectedUnite === 'Unité') {
                handleConditionnementChange({
                  target: {
                    name: 'quantite_unitaire',
                    value: '1'
                  }
                });
              }
            }}
            label="Unité"
          >
            <MenuItem value="unite">Unité</MenuItem>
            <MenuItem value="caisse">Caisse</MenuItem>
            <MenuItem value="carton">Carton</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={2}>
        <TextField
          fullWidth
          label="Quantité"
          name="quantite_unitaire"
          type="number"
          value={currentConditionnement.quantite_unitaire}
          onChange={(e) => {
            if (currentConditionnement.unite === 'Unité') {
              return;
            }
            handleConditionnementChange(e);
          }}
          size="small"
          inputProps={{ step: "0.01", min: "0.01" }}
          disabled={currentConditionnement.unite === 'Unité'}
        />
      </Grid>
      
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="Prix achat"
          name="prix_achat"
          type="number"
          value={currentConditionnement.prix_achat}
          onChange={handleConditionnementChange}
          size="small"
          InputProps={{
            endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
          }}
          inputProps={{ step: "0.01", min: "0" }}
        />
      </Grid>
      
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="Prix vente"
          name="prix_vente"
          type="number"
          value={currentConditionnement.prix_vente}
          onChange={handleConditionnementChange}
          size="small"
          InputProps={{
            endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
          }}
          inputProps={{ step: "0.01", min: "0" }}
        />
      </Grid>
      
      <Grid item xs={12} sm={1}>
        <Tooltip title="Ajouter le conditionnement">
          <IconButton
            color="primary"
            onClick={addConditionnement}
            sx={{ mt: 1 }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  </Paper>

  {conditionnements.length > 0 ? (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Unité</TableCell>
            <TableCell align="center">Quantité</TableCell>
            <TableCell align="right">Prix Achat</TableCell>
            <TableCell align="right">Prix Vente</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conditionnements.map((cond, index) => (
            <TableRow key={index}>
              <TableCell>
                <Chip label={cond.unite} size="small" />
              </TableCell>
              <TableCell align="center">{cond.quantite_unitaire}</TableCell>
              <TableCell align="right">{cond.prix_achat} HTG</TableCell>
              <TableCell align="right">{cond.prix_vente} HTG</TableCell>
              <TableCell align="center">
                <Tooltip title="Modifier">
                  <IconButton
                    size="small"
                    onClick={() => editConditionnement(index)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeConditionnement(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Alert severity="info" sx={{ mt: 2 }}>
      Aucun conditionnement défini. Le produit sera vendu à l'unité.
    </Alert>
  )}
</Grid> */}

                {/* Prix */}
                {/* <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Prix
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Prix d'achat"
                    name="prix_achat"
                    type="number"
                    value={formData.prix_achat}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Prix de vente"
                    name="prix_vente"
                    type="number"
                    value={formData.prix_vente}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid> */}

                {/* Stock */}
                {/* <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Gestion du stock
                  </Typography>
                </Grid> */}

                {/* <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Stock actuel"
                    name="stock_actuel"
                    type="number"
                    value={formData.stock_actuel}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: "0" }}
                  />
                </Grid> */}

                {/* <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Stock minimum"
                    name="stock_minimum"
                    type="number"
                    value={formData.stock_minimum}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    disabled={loading}
                    inputProps={{ min: "0" }}
                    helperText="Alerte quand le stock descend en dessous"
                  />
                </Grid> */}

              </Grid>
            </Grid>
          </Grid>

          {/* <Box mt={3}>
            <Typography variant="caption" color="textSecondary">
              * Champs obligatoires
            </Typography>
          </Box> */}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer le produit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProduitForm;